import { MessageType } from "../../../types/conversationInbox";
import type { ReactionType } from "../../../types/conversationInbox";

import type {
  InboxV2ConversationMessage,
  InboxV2MessageAttachment,
  InboxV2MessageReaction,
  InboxV2Sentiment,
} from "../../types";
import type {
  InboxV2RawAttachment,
  InboxV2RawConvLastMessage,
  InboxV2RawLinkedInCommentMessage,
  InboxV2RawMessage,
} from "../real-api/types";
import {
  isInboxV2CommentMessageKind,
  isInboxV2DirectMessageKind,
  isInboxV2MessageKindRenderable,
  isInboxV2PostSentimentMessageKind,
  isInboxV2SystemMessageKind,
} from "../../ui-meta/message-meta";
import { formatInboxV2Timestamp } from "../../utils/date-format";

import {
  InboxV2MessageMappingOptions,
  mapInboxV2Identity,
} from "./interlocutor";

export function shouldDisplayInboxV2Message(message: InboxV2RawMessage) {
  return isInboxV2MessageKindRenderable(
    getInboxV2MessageKind(message.type, {
      isInternal: shouldTreatInboxV2MessageAsInternal(message),
    })
  );
}

export function shouldStoreInboxV2Message(message: InboxV2RawMessage) {
  const kind = getInboxV2MessageKind(message.type, {
    isInternal: shouldTreatInboxV2MessageAsInternal(message),
  });

  return (
    isInboxV2MessageKindRenderable(kind) ||
    isInboxV2PostSentimentMessageKind(kind)
  );
}

function shouldTreatInboxV2MessageAsInternal(message: {
  isInternal?: boolean;
  type: MessageType | string;
}) {
  if (
    message.type === MessageType.system ||
    message.type === MessageType.typing ||
    message.type === MessageType.messageCreating
  ) {
    return false;
  }

  return Boolean(message.isInternal) || message.type === MessageType.internal;
}

export function mapMessageToInboxV2Message(
  message: InboxV2RawMessage,
  options?: InboxV2MessageMappingOptions
): InboxV2ConversationMessage {
  const sender = mapInboxV2Identity(message.sender, {
    account: options?.account,
    sentFromSked: Boolean(message.sentFromSked),
  });
  const story = mapMessageStory(message);
  const attachments = mapMessageAttachments(message);

  const baseMessage = {
    kind: getInboxV2MessageKind(message.type, {
      isInternal: shouldTreatInboxV2MessageAsInternal(message),
    }),
    sourceType: message.type,
    id: message.id,
    accountId: options?.conversationAccountId,
    conversationId: undefined,
    platformMessageId: message.platformId ?? undefined,
    authorId: sender.id,
    authorName: sender.name,
    authorHandle: sender.handle,
    authorPlatform: sender.platform,
    authorPlatformId: sender.platformId,
    authorFetchedError: sender.fetchedError,
    authorIsUnknownSource: sender.isUnknownSource,
    avatarUrl: sender.avatarUrl,
    createdAt: String(message.createdAt),
    createdAtLabel: formatInboxV2Timestamp(message.createdAt),
    body: message.text ?? "",
    isBrand: sender.isConversationAccountSender,
    isConversationAccountSender: sender.isConversationAccountSender,
    sentFromSked: sender.sentFromSked,
    sentFromSkedUserName: sender.sentFromSkedUserName,
  };

  if (isInboxV2SystemMessageKind(baseMessage.kind)) {
    return {
      ...baseMessage,
      kind: baseMessage.kind,
      isSystem: true,
    };
  }

  if (baseMessage.kind === "message-creating") {
    return {
      ...baseMessage,
      kind: "message-creating",
      sendStatus: "sending",
      subKind: message.isInternal ? "internal" : "social",
      targetLayout: "comment",
    };
  }

  if (baseMessage.kind === "internal") {
    return {
      ...baseMessage,
      kind: "internal",
    };
  }

  if (isInboxV2DirectMessageKind(baseMessage.kind)) {
    return {
      ...baseMessage,
      kind: baseMessage.kind,
      story,
      attachments:
        attachments.length > 0 ||
        story ||
        !isEmptyPlatformAttachmentMessage(message)
          ? attachments
          : [{ type: "unknown", unavailable: true }],
      reactions: mapMessageReactions(message, options),
      commentRef: message.commentRef,
      directReplyToMessageId: message.commentRef?.dbId,
    };
  }

  if (isInboxV2CommentMessageKind(baseMessage.kind)) {
    return {
      ...baseMessage,
      kind: baseMessage.kind,
      sentiment: mapSentimentTypeToInboxV2Sentiment(message.sentiment?.type),
      isDeleted: Boolean(message.deleted),
      isHidden: Boolean(message.hidden),
      directMessageId: message.directMessageId,
      attachments,
      likeCount: message.likes,
      reactions: mapMessageReactions(message, options),
      threadedLevel: message.level,
    };
  }

  if (!isInboxV2MessageKindRenderable(baseMessage.kind)) {
    return {
      ...baseMessage,
      kind: baseMessage.kind,
      sentiment: mapSentimentTypeToInboxV2Sentiment(message.sentiment?.type),
    };
  }

  return {
    ...baseMessage,
    kind: "unknown",
    sentiment: mapSentimentTypeToInboxV2Sentiment(message.sentiment?.type),
    isSystem: message.type === MessageType.system,
    isDeleted: Boolean(message.deleted),
    isHidden: Boolean(message.hidden),
    story,
    attachments,
    likeCount: message.likes,
    reactions: mapMessageReactions(message, options),
  };
}

function getInboxV2MessageKind(
  messageType: MessageType | string,
  options?: { isInternal?: boolean }
): InboxV2ConversationMessage["kind"] {
  switch (messageType) {
    case MessageType.system:
      return "system";
    case MessageType.typing:
      return "typing";
    case MessageType.messageCreating:
      return "message-creating";
  }

  if (options?.isInternal) {
    return "internal";
  }

  switch (messageType) {
    case MessageType.fbComment:
      return "fb-comment";
    case MessageType.fbThreadedComment:
      return "fb-threaded-comment";
    case MessageType.fbMentionV2:
      return "fb-mention-v2";
    case MessageType.igComment:
      return "ig-comment";
    case MessageType.igThreadedComment:
      return "ig-threaded-comment";
    case MessageType.liComment:
      return "li-comment";
    case MessageType.liThreadedComment:
      return "li-threaded-comment";
    case MessageType.ttThreadedComment:
      return "tt-threaded-comment";
    case MessageType.gmbReviewReply:
      return "gmb-review-reply";
    case MessageType.gmbAnwser:
      return "gmb-answer";
    case MessageType.igMention:
      return "ig-mention";
    case MessageType.igMentionV2:
      return "ig-mention-v2";
    case MessageType.fbMessage:
      return "fb-message";
    case MessageType.igMessage:
      return "ig-message";
    case MessageType.igStoryMention:
      return "ig-story-mention";
    case MessageType.igStoryReply:
      return "ig-story-reply";
    case MessageType.fbPost:
      return "fb-post";
    case MessageType.fbMentionInPostV2:
      return "fb-mention-in-post-v2";
    case MessageType.igPost:
      return "ig-post";
    case MessageType.igMentionPost:
      return "ig-mention-post";
    case MessageType.igMentionInPostV2:
      return "ig-mention-in-post-v2";
    case MessageType.liPost:
      return "li-post";
    case MessageType.internal:
      return "internal";
    default:
      return "unknown";
  }
}

function isEmptyPlatformAttachmentMessage(message: InboxV2RawMessage) {
  return (
    (message.type === MessageType.fbMessage ||
      message.type === MessageType.igMessage) &&
    !(message.text ?? "").trim()
  );
}

function mapMessageReactions(
  message: InboxV2RawMessage,
  options?: InboxV2MessageMappingOptions
): InboxV2MessageReaction[] {
  if (message.type === MessageType.ttThreadedComment) {
    return message.liked
      ? [
          {
            id: message.id,
            reactee: message.accountId ?? options?.conversationAccountId ?? "",
            reaction: "love",
            createdAt: String(message.createdAt),
          },
        ]
      : [];
  }

  return (message.reactions ?? []).map((element) =>
    mapReactionToInboxV2Reaction(element)
  );
}

function mapMessageStory(
  message: InboxV2RawMessage
): InboxV2MessageAttachment | undefined {
  if (!message.story?.url) {
    return undefined;
  }

  return {
    type: mapAttachmentType(message.story.type),
    title: "Instagram story",
    url: message.story.url,
  };
}

function mapMessageAttachments(
  message: InboxV2RawMessage
): InboxV2MessageAttachment[] {
  const attachments: InboxV2MessageAttachment[] = [];

  if (message.attachment) {
    const mediaImageUrl = message.attachment.media?.image?.src;
    const mediaSourceUrl = message.attachment.media?.source;

    if (mediaImageUrl) {
      attachments.push({ type: "image", url: mediaImageUrl });
    } else if (mediaSourceUrl) {
      attachments.push({
        type: mapAttachmentType(message.attachment.type),
        url: mediaSourceUrl,
      });
    } else if (message.attachment.url) {
      attachments.push({
        type: mapAttachmentType(message.attachment.type),
        url: message.attachment.url,
      });
    } else {
      attachments.push({
        type: mapAttachmentType(message.attachment.type),
      });
    }
  }

  for (const attachment of message.attachments ?? []) {
    attachments.push(mapInboxV2DirectAttachment(attachment));
  }

  for (const attachment of getLinkedInCommentContentAttachments(message)) {
    attachments.push(attachment);
  }

  return deduplicateStickerAttachments(attachments);
}

function deduplicateStickerAttachments(
  attachments: InboxV2MessageAttachment[]
): InboxV2MessageAttachment[] {
  const stickerUrls = new Set(
    attachments.flatMap((attachment) =>
      attachment.type === "sticker" && attachment.url ? [attachment.url] : []
    )
  );
  const seenStickerUrls = new Set<string>();

  return attachments.filter((attachment) => {
    if (!attachment.url) {
      return true;
    }

    if (attachment.type !== "sticker") {
      return !stickerUrls.has(attachment.url);
    }

    if (seenStickerUrls.has(attachment.url)) {
      return false;
    }

    seenStickerUrls.add(attachment.url);
    return true;
  });
}

function mapAttachmentType(type: string): InboxV2MessageAttachment["type"] {
  const normalizedType = type.toLowerCase();

  if (normalizedType === "audio") {
    return "audio";
  }

  if (normalizedType === "sticker") {
    return "sticker";
  }

  if (normalizedType === "share") {
    return "share";
  }

  if (normalizedType === "fallback") {
    return "fallback";
  }

  if (normalizedType === "ig_reel" || normalizedType === "reel") {
    return "reel";
  }

  if (normalizedType === "ig_post" || normalizedType === "post") {
    return "post";
  }

  if (normalizedType === "ig_story" || normalizedType === "story_mention") {
    return "story";
  }

  if (normalizedType === "appointment_booking") {
    return "appointment-booking";
  }

  if (normalizedType === "template") {
    return "template-product";
  }

  if (normalizedType === "ephemeral") {
    return "ephemeral";
  }

  if (normalizedType.includes("image") || normalizedType.includes("photo")) {
    return "image";
  }

  if (
    normalizedType.includes("video") ||
    normalizedType.includes("animated_image")
  ) {
    return "video";
  }

  if (normalizedType.includes("file")) {
    return "file";
  }

  return "unknown";
}

function mapInboxV2DirectAttachment(
  attachment: InboxV2RawAttachment
): InboxV2MessageAttachment {
  switch (attachment.type) {
    case "ig_story":
      return {
        type: "story",
        storyMediaId: attachment.payload?.story_media_id,
        title: "Instagram story",
        url: attachment.payload?.story_media_url,
      };
    case "story_mention":
      return {
        type: "story",
        title: "Instagram story",
        url: attachment.payload?.url,
      };
    case "ig_reel":
    case "reel":
      return {
        reelVideoId: attachment.payload?.reel_video_id,
        type: "reel",
        title: attachment.payload?.title || "Instagram reel",
        url: attachment.payload?.url,
      };
    case "ig_post":
      return {
        postMediaId: attachment.payload?.ig_post_media_id,
        type: "post",
        title: attachment.payload?.title || "Shared post",
        url: attachment.payload?.url,
      };
    case "post":
      return {
        postId: attachment.payload?.id,
        type: "post",
        title: attachment.payload?.title || "Shared post",
        url: attachment.payload?.url,
      };
    case "audio":
      return {
        type: "audio",
        title: "Audio clip",
        url: attachment.payload?.url,
      };
    case "share":
      return {
        type: "share",
        title: "Shared content",
        url: attachment.payload?.url,
      };
    case "fallback":
      return {
        type: "fallback",
        title: attachment.payload?.title || "Shared link",
        url: attachment.payload?.url,
      };
    case "appointment_booking":
      return {
        type: "appointment-booking",
        bookingEndTime: attachment.payload?.end_time,
        bookingId: attachment.payload?.booking_id,
        bookingStartTime: attachment.payload?.start_time,
        bookingStatus: attachment.payload?.status,
        bookingTimezone: attachment.payload?.timezone,
        title: "Appointment booking",
      };
    case "template": {
      const product = attachment.payload?.product?.elements?.[0];

      return {
        type: "template-product",
        imageUrl: product?.image_url,
        productElementId: product?.id,
        retailerId: product?.retailer_id,
        subtitle: product?.subtitle,
        title: product?.title || "Product referral",
      };
    }
    case "ephemeral":
      return {
        type: "ephemeral",
        title: "Ephemeral attachment",
      };
    case "image":
    case "video":
    case "file":
    case "sticker":
    case "unsupported_type":
    default:
      return {
        type: mapAttachmentType(attachment.type),
        title: undefined,
        url: attachment.payload?.url,
      };
  }
}

function getLinkedInCommentContentAttachments(
  message: InboxV2RawMessage
): InboxV2MessageAttachment[] {
  if (
    message.type !== MessageType.liComment &&
    message.type !== MessageType.liThreadedComment
  ) {
    return [];
  }

  const content = (message as InboxV2RawLinkedInCommentMessage).content;

  if (!Array.isArray(content)) {
    return [];
  }

  return content.map((element) => ({
    type: mapAttachmentType(element.type),
    url: element.url,
  }));
}

export function mapReactionToInboxV2Reaction(
  reaction: ReactionType
): InboxV2MessageReaction {
  return {
    id: reaction.id,
    reactee: reaction.reactee,
    reaction:
      reaction.reaction as unknown as InboxV2MessageReaction["reaction"],
    emoji: reaction.emoji,
    createdAt: reaction.createdAt,
  };
}

export function mapLastMessageToInboxV2Message(
  message: InboxV2RawConvLastMessage | undefined,
  conversationId: string,
  options?: InboxV2MessageMappingOptions
): InboxV2ConversationMessage | undefined {
  if (!message) {
    return undefined;
  }

  const sender = mapInboxV2Identity(message.sender, {
    account: options?.account,
  });
  const kind = getInboxV2MessageKind(message.type, {
    isInternal: shouldTreatInboxV2MessageAsInternal(message),
  });

  const baseMessage = {
    kind,
    sourceType: message.type,
    id: message.id,
    accountId: options?.conversationAccountId,
    conversationId,
    platformMessageId: message.platformId ?? undefined,
    authorName: sender.name,
    authorHandle: sender.handle,
    authorPlatform: sender.platform,
    authorPlatformId: sender.platformId,
    avatarUrl: sender.avatarUrl,
    createdAt: String(message.createdAt),
    createdAtLabel: formatInboxV2Timestamp(message.createdAt),
    body: message.text ?? "",
    isBrand: sender.isConversationAccountSender,
    isConversationAccountSender: sender.isConversationAccountSender,
    sentFromSked: sender.sentFromSked,
    sentFromSkedUserName: sender.sentFromSkedUserName,
  };

  if (isInboxV2CommentMessageKind(kind)) {
    return {
      ...baseMessage,
      kind,
      sentiment: "unknown",
      isDeleted: Boolean(message.deleted),
      reactions: [],
    };
  }

  if (isInboxV2DirectMessageKind(kind)) {
    return {
      ...baseMessage,
      kind,
      reactions: [],
    };
  }

  if (kind === "internal") {
    return {
      ...baseMessage,
      kind: "internal",
    };
  }

  if (isInboxV2SystemMessageKind(kind)) {
    return {
      ...baseMessage,
      kind,
      isSystem: true,
    };
  }

  if (kind === "message-creating") {
    return {
      ...baseMessage,
      kind: "message-creating",
      sendStatus: "sending",
      subKind: shouldTreatInboxV2MessageAsInternal(message)
        ? "internal"
        : "social",
      targetLayout: "comment",
    };
  }

  if (!isInboxV2MessageKindRenderable(kind)) {
    return {
      ...baseMessage,
      kind,
    };
  }

  return {
    ...baseMessage,
    kind: "unknown",
    sentiment: "unknown",
    isSystem: message.type === MessageType.system,
    isDeleted: Boolean(message.deleted),
    reactions: [],
  };
}

export function mapSentimentTypeToInboxV2Sentiment(
  sentimentType?: number
): InboxV2Sentiment {
  switch (sentimentType) {
    case 1: {
      return "positive";
    }
    case 2: {
      return "negative";
    }
    case 3: {
      return "neutral";
    }
    case 4: {
      return "mixed";
    }
    case 5:
    default: {
      return "unknown";
    }
  }
}
